import { useCallback, useEffect, useRef, useState } from 'react';

import { Dropdown, DropdownDivider, DropdownItem } from '../common';
import { usePrompts } from '../../context/PromptContext';
import { ImageTargetBar, TargetNotice } from './ImageTarget';
import './chat.css';

/**
 * The composer, which is where the three prompt sources meet.
 *
 * - Attribute mode on, box empty → the extraction prompt is generated from the
 *   version's attribute set. This is the training-parity path.
 * - A saved prompt selected → that prompt is used, with variables filled in.
 * - Anything typed → the typed text wins over the selection, always. The app never
 *   silently substitutes a prompt for what the user actually wrote.
 */
export function Composer({
  onSend,
  onStop,
  streaming,
  attachments,
  onFiles,
  onRemoveAttachment,
  attributeMode,
  onAttributeModeChange,
  disabled = false,
  disabledReason = '',
  hasGroundingImage = false,
  groundingLabel = '',
  attributeCount = 0,
  images = [],
  target = null,
  onTargetChange = () => {},
  resolvedTarget = null,
}) {
  const { prompts, selected, setSelectedId } = usePrompts();
  const [text, setText] = useState('');
  const [dropping, setDropping] = useState(false);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const autoGrow = useCallback(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 168)}px`;
  }, []);

  useEffect(autoGrow, [text, autoGrow]);

  const canSend =
    !disabled && !streaming && (attachments.length > 0 || hasGroundingImage || text.trim());

  // Named in the placeholder as well as on the chip, so the scope of what is about
  // to be sent is visible in the box being typed into rather than only above it.
  // Not `selected`: that is the prompt selection from usePrompts, a few lines up.
  const targetImage = target ? images.find((image) => image.image_id === target) : null;
  const targetLabel = targetImage
    ? `image ${targetImage.index}${targetImage.filename ? ` (${targetImage.filename})` : ''}`
    : '';

  const submit = () => {
    if (!canSend) return;
    // The toggle decides whether the answer is parsed; typed text only replaces
    // the prompt body. Keeping those independent means the mode never changes
    // silently just because the user typed something.
    onSend({
      promptText: text.trim() || null,
      promptId: text.trim() ? null : selected?.id || null,
      mode: attributeMode ? 'extraction' : 'chat',
    });
    setText('');
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDropping(false);
    if (event.dataTransfer?.files?.length) onFiles(event.dataTransfer.files);
  };

  const onPaste = (event) => {
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length) {
      event.preventDefault();
      onFiles(files);
    }
  };

  return (
    <div
      className={`composer ${dropping ? 'composer--drop' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={onDrop}
    >
      <div className="composer__bar">
        <button
          type="button"
          className={`chip chip--button ${attributeMode ? 'chip--accent' : ''}`}
          onClick={() => onAttributeModeChange(!attributeMode)}
          title={
            attributeMode
              ? 'The generated extraction prompt will be sent when the box is empty'
              : 'Responses will not be parsed into attributes'
          }
        >
          <span aria-hidden="true">▤</span>
          Attribute extraction {attributeMode ? 'on' : 'off'}
          {attributeMode && attributeCount ? ` · ${attributeCount}` : ''}
        </button>

        <Dropdown
          trigger={({ toggle }) => (
            <button type="button" className="chip chip--button" onClick={toggle}>
              Prompt: {selected ? selected.name : 'generated'} ▾
            </button>
          )}
          render={({ close }) => (
            <>
              <DropdownItem
                active={!selected}
                onClick={() => {
                  setSelectedId(null);
                  close();
                }}
              >
                Generated from attribute set
              </DropdownItem>
              <DropdownDivider />
              {prompts.map((prompt) => (
                <DropdownItem
                  key={prompt.id}
                  active={prompt.id === selected?.id}
                  onClick={() => {
                    setSelectedId(prompt.id);
                    onAttributeModeChange(prompt.is_extraction);
                    close();
                  }}
                >
                  <span className="grow truncate">{prompt.name}</span>
                  {prompt.is_extraction && <span className="xs subtle">extraction</span>}
                </DropdownItem>
              ))}
            </>
          )}
        />

        <button
          type="button"
          className="chip chip--button"
          onClick={() => fileRef.current?.click()}
        >
          ＋ Image
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          onChange={(event) => {
            onFiles(event.target.files);
            event.target.value = '';
          }}
        />

        {streaming && (
          <button type="button" className="chip chip--button" onClick={onStop}>
            ■ Stop
          </button>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="composer__attachments">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="attachment">
              <img src={attachment.previewUrl} alt={attachment.filename} />
              <button
                type="button"
                className="attachment__remove"
                onClick={() => onRemoveAttachment(attachment.id)}
                aria-label={`Remove ${attachment.filename}`}
              >
                ✕
              </button>
              <div className="attachment__name">{attachment.filename}</div>
            </div>
          ))}
        </div>
      )}

      <ImageTargetBar
        images={images}
        value={target}
        onChange={onTargetChange}
        resolved={resolvedTarget}
        disabled={disabled || streaming}
      />

      <div className="composer__box">
        <textarea
          ref={inputRef}
          className="composer__input"
          rows={1}
          value={text}
          placeholder={
            disabled
              ? disabledReason || 'Unavailable'
              : targetLabel
                ? `Ask about ${targetLabel}…`
                : hasGroundingImage
                  ? 'Ask a follow-up about this image…'
                  : attributeMode
                    ? 'Drop a road image, then press send to extract all attributes — or type a question instead'
                    : 'Drop an image and ask a question…'
          }
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        <button
          type="button"
          className="composer__send"
          onClick={submit}
          disabled={!canSend}
          aria-label="Send"
          title={canSend ? 'Send (Enter)' : disabledReason || 'Attach an image or type a question'}
        >
          ↑
        </button>
      </div>

      <div className="composer__foot">
        {/* What the server did with the previous question, when that is not obvious
            from the chips — a reference read out of the user's own prose, or one it
            could not honour. */}
        <TargetNotice resolved={resolvedTarget} images={images} />
        {hasGroundingImage && groundingLabel && !targetLabel && (
          <span>
            Grounded on <strong style={{ color: 'var(--fg-muted)' }}>{groundingLabel}</strong> —
            follow-ups keep the image in context
          </span>
        )}
        {!hasGroundingImage && attributeMode && (
          <span>Enter sends · Shift+Enter for a new line · paste or drop images</span>
        )}
        {text.trim() && (
          <span style={{ color: 'var(--state-warn)' }}>
            Your text replaces the prompt ·{' '}
            {attributeMode ? 'response will be parsed' : 'plain answer'}
          </span>
        )}
        {disabled && disabledReason && (
          <span style={{ color: 'var(--state-warn)' }}>{disabledReason}</span>
        )}
      </div>
    </div>
  );
}
